/**
 * Spettri-specific event helpers.
 * 
 * Thin wrapper around the generic dispatchEvent function for Spettri game events.
 * Spettri has three event types:
 * - 'submit': Submit current arrangement for validation
 * - 'hint_reveal_titles': Reveal the titles/themes of both spectra
 * - 'hint_arrange_columns': Move items to their correct spectrum columns
 * 
 * Usage:
 * ```javascript
 * const { dispatchEvent, dispatchHintEvent } = useGameplaySession('spettri', date);
 * const { submitArrangement, revealTitles, arrangeColumns } = useSpettriEvents(dispatchEvent, dispatchHintEvent);
 * 
 * const result = await submitArrangement(spectrum0, spectrum1);
 * if (result.success) {
 *   // result.result contains: { complete, feedback, lives_remaining, answers }
 * }
 * ```
 */

import { useCallback } from 'react';

/**
 * Hook providing Spettri-specific event helpers.
 * 
 * @param {function} dispatchEvent - The dispatchEvent function from useGameplaySession
 * @param {function} dispatchHintEvent - The dispatchHintEvent function from useGameplaySession
 * @returns {object} Spettri event helpers
 * @returns {function} returns.submitArrangement - Submit current spectrum arrangement
 * @returns {function} returns.revealTitles - Request to reveal spectrum titles (hint)
 * @returns {function} returns.arrangeColumns - Request to arrange items in correct columns (hint)
 */
export function useSpettriEvents(dispatchEvent, dispatchHintEvent) {
  /**
   * Submit current spectrum arrangement for validation.
   * 
   * Note: The frontend now stores spectra in BACKEND ORDER (index 0 = lowest value).
   * No reversal needed before sending.
   * 
   * @param {string[]} spectrum0 - Items in spectrum 0 (backend order, 5 items)
   * @param {string[]} spectrum1 - Items in spectrum 1 (backend order, 5 items)
   * @returns {Promise<{
   *   success: boolean,
   *   result?: {
   *     complete: boolean,
   *     feedback: Array<Array<{correct: boolean}>>,
   *     lives_remaining: number,
   *     answers?: Array<{title: string, items: string[]}>
   *   },
   *   state?: object,
   *   error?: string,
   *   message?: string
   * }>}
   * 
   * @example
   * const result = await submitArrangement(
   *   ['lowest', 'low', 'mid', 'high', 'highest'],
   *   ['smallest', 'small', 'medium', 'big', 'biggest']
   * );
   * if (result.success) {
   *   if (result.result.complete) {
   *     // All correct - game won!
   *   } else {
   *     // Check result.result.feedback for per-item feedback
   *     // result.state.lives shows remaining lives
   *   }
   * }
   */
  const submitArrangement = useCallback(async (spectrum0, spectrum1) => {
    // Spectra are already in backend order, send directly
    return dispatchEvent('submit', {
      spectrum_0: spectrum0,
      spectrum_1: spectrum1,
    });
  }, [dispatchEvent]);

  /**
   * Request to reveal spectrum titles (hint).
   * Requires authentication and sufficient gettoncini balance.
   * 
   * @param {function} onNotAuthenticated - Callback when user is not signed in
   * @returns {Promise<{
   *   success: boolean,
   *   hint_data?: { titles: [string, string] },
   *   state?: object,
   *   error?: string,
   *   message?: string
   * }>}
   * 
   * @example
   * const result = await revealTitles(() => showSignupModal());
   * if (result.success) {
   *   const [title0, title1] = result.hint_data.titles;
   * }
   */
  const revealTitles = useCallback(async (onNotAuthenticated = null) => {
    return dispatchHintEvent('hint_reveal_titles', {}, onNotAuthenticated);
  }, [dispatchHintEvent]);

  /**
   * Request to move items to their correct spectrum columns (hint).
   * Requires authentication and sufficient gettoncini balance.
   * 
   * The server determines which items are locked by:
   * - Fixed positions from the puzzle config
   * - Correct positions from previous submission feedback
   * 
   * The server moves items to their correct columns (in random order within
   * movable positions) and returns the updated state. The client just needs
   * to use the updated spectrum_0 and spectrum_1 from response.state.
   * 
   * @param {function} onNotAuthenticated - Callback when user is not signed in
   * @returns {Promise<{
   *   success: boolean,
   *   state?: { spectrum_0: string[], spectrum_1: string[], ... },
   *   result?: { column_items: { s0: string[], s1: string[] } },
   *   error?: string,
   *   message?: string
   * }>}
   * 
   * @example
   * const result = await arrangeColumns(() => showSignupModal());
   * if (result.success) {
   *   // Use the updated spectra from state
   *   setSpectrum0(result.state.spectrum_0);
   *   setSpectrum1(result.state.spectrum_1);
   * }
   */
  const arrangeColumns = useCallback(async (onNotAuthenticated = null) => {
    // No event data needed - server derives locked positions from state and config
    return dispatchHintEvent('hint_arrange_columns', {}, onNotAuthenticated);
  }, [dispatchHintEvent]);

  return {
    submitArrangement,
    revealTitles,
    arrangeColumns,
  };
}

